/**
 * The player's drivable vehicle.
 *
 * Owns the enter/exit state machine, the collision resolution and the parked
 * position that gets saved. The handling itself lives in `CarDynamics`, shared
 * with traffic (ADR-0011).
 *
 * The car is not spawned when it is bought — it is *always* in the world if
 * you own one, parked wherever you left it. That is the whole point: a vehicle
 * that appears when needed and vanishes afterwards is a menu with wheels.
 */
import * as THREE from 'three';
import type { Collider } from '../world/Neighbourhood.js';
import { WORLD_BOUNDS } from '../../sim/data/world.js';
import {
  createCarState, stepCar, carCorners, clamp,
  PLAYER_CAR, type CarState, type CarInput,
} from '../world/CarDynamics.js';

/** How close the player must be to the driver's door to get in. */
export const ENTER_RADIUS = 3.4;

export interface ParkedVehicle {
  x: number;
  z: number;
  heading: number;
}

export interface VehicleControllerOptions {
  /** The visual to drive. Its transform is written every frame. */
  group: THREE.Group;
}

export interface VehicleController {
  readonly state: CarState;
  readonly driving: boolean;
  /** Where the player stands when they get out. */
  exitPoint(): { x: number; z: number };
  /** True when the player is close enough, on foot, to get in. */
  canEnter(playerX: number, playerZ: number): boolean;
  enter(): void;
  exit(): void;
  /** Place the car, e.g. from a save or on first purchase. */
  place(spot: ParkedVehicle): void;
  /** Serialisable parked position. */
  parked(): ParkedVehicle;
  update(dt: number, input: CarInput, colliders: Collider[]): void;
  /** 0..1 against top speed, for the camera and the engine note. */
  readonly speedFraction: number;
  /** Metres travelled since the last call. Drives fuel and odometer costs. */
  takeDistance(): number;
}

export function createVehicleController(
  options: VehicleControllerOptions,
): VehicleController {
  const car = createCarState(0, 0, 0);
  let driving = false;
  let distanceSinceRead = 0;
  // Visual lean into corners. Not suspension - ADR-0011 is explicit that there
  // is none - just enough body roll that a turn reads as a turn.
  let roll = 0;

  function writeTransform(): void {
    options.group.position.set(car.x, 0, car.z);
    options.group.rotation.set(0, car.heading, roll);
  }

  function update(dt: number, input: CarInput, colliders: Collider[]): void {
    if (!driving) return;

    const before = { x: car.x, z: car.z };
    const wanted = stepCar(car, input, PLAYER_CAR, dt);

    // Axis-separated, exactly as the player's own movement resolves. Sliding
    // along a wall is what stops a glancing blow becoming a dead stop, and a
    // car that halts dead on a kerb it clipped feels broken in a way that a
    // car which scrapes along it does not.
    if (!blocked(wanted.x, car.z, car.heading, colliders)) {
      car.x = wanted.x;
    } else {
      car.speed *= -0.12;
    }
    if (!blocked(car.x, wanted.z, car.heading, colliders)) {
      car.z = wanted.z;
    } else {
      car.speed *= -0.12;
    }

    car.x = clamp(car.x, WORLD_BOUNDS.minX + 3, WORLD_BOUNDS.maxX - 3);
    car.z = clamp(car.z, WORLD_BOUNDS.minZ + 3, WORLD_BOUNDS.maxZ - 3);

    distanceSinceRead += Math.hypot(car.x - before.x, car.z - before.z);

    const targetRoll = -car.steer * Math.min(1, Math.abs(car.speed) / PLAYER_CAR.maxSpeed) * 0.05;
    roll += (targetRoll - roll) * Math.min(1, dt * 6);
    writeTransform();
  }

  return {
    state: car,
    get driving() { return driving; },
    get speedFraction() { return Math.min(1, Math.abs(car.speed) / PLAYER_CAR.maxSpeed); },

    canEnter(playerX: number, playerZ: number): boolean {
      if (driving) return false;
      return Math.hypot(playerX - car.x, playerZ - car.z) < ENTER_RADIUS;
    },

    enter(): void {
      driving = true;
    },

    exit(): void {
      driving = false;
      // Leaving the car at speed and having it coast away driverless is a
      // funny bug exactly once.
      car.speed = 0;
      car.steer = 0;
      writeTransform();
    },

    exitPoint(): { x: number; z: number } {
      // Out of the driver's door, on the left of the car, which is where the
      // door is on the model.
      const c = Math.cos(car.heading);
      const s = Math.sin(car.heading);
      return {
        x: car.x + c * (PLAYER_CAR.halfWidth + 1.1),
        z: car.z - s * (PLAYER_CAR.halfWidth + 1.1),
      };
    },

    place(spot: ParkedVehicle): void {
      car.x = spot.x;
      car.z = spot.z;
      car.heading = spot.heading;
      car.speed = 0;
      car.steer = 0;
      writeTransform();
    },

    parked(): ParkedVehicle {
      return { x: car.x, z: car.z, heading: car.heading };
    },

    update,

    takeDistance(): number {
      const d = distanceSinceRead;
      distanceSinceRead = 0;
      return d;
    },
  };
}

/**
 * Whether the car's body would intersect anything solid at this position.
 *
 * Tests the four corners rather than a circle. A car is two and a half times
 * longer than it is wide, so a radius big enough to protect the nose stops the
 * car a metre short of every wall, and one small enough to park with lets the
 * nose pass through them.
 */
function blocked(
  x: number, z: number, heading: number, colliders: Collider[],
): boolean {
  const corners = carCorners({ x, z, heading }, PLAYER_CAR);
  for (const c of colliders) {
    // Soft colliders keep the camera out of foliage; driving through a hedge
    // is between the player and the hedge.
    if (c.soft) continue;
    for (const [cx, cz] of corners) {
      if (cx > c.minX && cx < c.maxX && cz > c.minZ && cz < c.maxZ) return true;
    }
  }
  return false;
}
